/**
 * Construye las opciones de ECharts para los donuts de la vista Semantica.
 *
 * Los graficos de dominios y de subdominios eran identicos salvo los datos y el
 * tooltip, y estaban duplicados dentro del componente. Tenerlos aqui mantiene
 * ambos niveles coherentes y saca ~70 lineas del componente.
 */
export interface DonutOptionsInput {
  seriesName: string;
  data: unknown[];
  isDark: boolean;
  tooltipFormatter: (params: any) => string;
}

/**
 * Las etiquetas se dibujan por fuera del anillo, asi que el radio deja margen
 * a proposito: con un radio mayor los nombres largos salian del area visible.
 */
const GEOMETRY = {
  radius: ['38%', '60%'] as [string, string],
  center: ['50%', '52%'] as [string, string],
};

/** Corta los nombres largos en varias lineas para que no invadan el grafico. */
function wrapLongLabel(params: any): string {
  return params.name.length > 16
    ? params.name.replaceAll(/(.{1,16})(\s|$)/g, '$1\n')
    : params.name;
}

export function buildDomainDonutOptions(input: DonutOptionsInput): any {
  const labelColor = input.isDark ? '#e7eaf4' : '#1f2937';
  const borderColor = input.isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.06)';
  const labelLineColor = input.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)';

  return {
    tooltip: {
      show: true,
      trigger: 'item',
      formatter: input.tooltipFormatter,
    },
    legend: { show: false },
    series: [
      {
        name: input.seriesName,
        type: 'pie',
        radius: GEOMETRY.radius,
        center: GEOMETRY.center,
        avoidLabelOverlap: false,
        itemStyle: { borderRadius: 8, borderColor, borderWidth: 2 },
        label: {
          show: true,
          position: 'outside',
          formatter: wrapLongLabel,
          fontWeight: 'bold',
          fontSize: 16,
          width: 120,
          overflow: 'break',
          color: labelColor,
        },
        labelLine: { length: 18, length2: 14, lineStyle: { color: labelLineColor } },
        labelLayout: { hideOverlap: true },
        emphasis: { label: { show: true, fontSize: 20, fontWeight: 'bold' } },
        data: input.data,
      },
    ],
  };
}

/** Etiqueta usada cuando un documento no declara subdominio. */
export const UNKNOWN_SUBDOMAIN = 'Sin subdominio';

/** Lee el subdominio admitiendo el nombre antiguo `subcategory`. */
function readSubdomain(node: any): string {
  return node.subdomain || node.subcategory || UNKNOWN_SUBDOMAIN;
}

export interface SubdomainSlices {
  names: string[];
  colorByName: { [name: string]: string };
  data: unknown[];
}

/**
 * Agrupa los nodos de un dominio por subdominio, en un solo recorrido, y les
 * asigna un color estable de la paleta.
 */
export function buildSubdomainSlices(nodes: unknown[], palette: string[]): SubdomainSlices {
  const countByName = new Map<string, number>();
  for (const node of nodes) {
    const name = readSubdomain(node);
    countByName.set(name, (countByName.get(name) ?? 0) + 1);
  }

  const names = [...countByName.keys()];
  const colorByName: { [name: string]: string } = {};
  names.forEach((name, i) => {
    colorByName[name] = palette[i % palette.length];
  });

  return {
    names,
    colorByName,
    data: names.map(name => ({
      name,
      value: countByName.get(name) ?? 0,
      itemStyle: { color: colorByName[name] },
    })),
  };
}
